/**
 * PatternIQ Admin - Governance Policies
 * ======================================
 * Phase C2: Governance & Policy Engine UI
 * 
 * Configure tenant-level policies for:
 * - Action limits (max impact, approval thresholds)
 * - Data access (retailer restrictions)
 * - Risk thresholds (confidence minimums)
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Shield, 
  DollarSign, 
  Database, 
  AlertTriangle,
  Save, 
  RotateCcw,
  CheckCircle,
  AlertCircle,
  Info
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Slider } from '../../components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

interface ActionLimitsPolicy {
  maxActionImpactDollars?: number;
  requireApprovalForImpactAbove?: number;
  forbidActionTypes?: string[];
  maxActionsPerDay?: number;
}

interface DataAccessPolicy {
  allowedRetailers?: string[];
  allowCrossRetailerView?: boolean;
}

interface RiskThresholdsPolicy {
  minRecommendationConfidence?: number;
  maxWOSThreshold?: number;
  minWOSThreshold?: number;
}

interface PoliciesResponse {
  success: boolean;
  companyId: string;
  policies: {
    action_limits: ActionLimitsPolicy;
    data_access: DataAccessPolicy;
    risk_thresholds: RiskThresholdsPolicy;
  };
  hasCustomPolicies: {
    action_limits: boolean;
    data_access: boolean;
    risk_thresholds: boolean;
  };
  defaults: {
    action_limits: ActionLimitsPolicy;
    data_access: DataAccessPolicy;
    risk_thresholds: RiskThresholdsPolicy;
  };
}

const ACTION_TYPES = [
  { id: 'dc_to_store', label: 'DC to Store Transfer' },
  { id: 'reorder', label: 'Reorder' },
  { id: 'transfer', label: 'Store Transfer' },
  { id: 'increase_price', label: 'Price Increase' },
  { id: 'decrease_price', label: 'Price Decrease' },
  { id: 'add_promo', label: 'Add Promotion' },
  { id: 'remove_promo', label: 'Remove Promotion' },
  { id: 'promo_discount_over_50', label: 'Discount > 50%' }
];

const RETAILERS = [
  { id: '10000000-0000-0000-0000-000000000001', label: 'Ulta Beauty' },
  { id: '10000000-0000-0000-0000-000000000003', label: "Macy's" },
  { id: '10000000-0000-0000-0000-000000000004', label: 'JCPenney' }
];

export default function Policies() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('action_limits');
  const [saveStatus, setSaveStatus] = useState<{ type: string; status: 'success' | 'error'; message: string } | null>(null);
  
  // Local state for editing
  const [actionLimits, setActionLimits] = useState<ActionLimitsPolicy>({});
  const [dataAccess, setDataAccess] = useState<DataAccessPolicy>({});
  const [riskThresholds, setRiskThresholds] = useState<RiskThresholdsPolicy>({});

  // Fetch policies
  const { data, isLoading, error } = useQuery<PoliciesResponse>({
    queryKey: ['tenant-policies'],
    queryFn: async () => {
      const res = await fetch('/api/policies/get');
      if (!res.ok) throw new Error('Failed to fetch policies');
      return res.json();
    }
  });

  // Initialize local state when data loads
  useEffect(() => {
    if (data?.policies) {
      setActionLimits(data.policies.action_limits || {});
      setDataAccess(data.policies.data_access || {});
      setRiskThresholds(data.policies.risk_thresholds || {});
    }
  }, [data]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async ({ policyType, config }: { policyType: string; config: any }) => {
      const res = await fetch('/api/policies/save', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ policyType, config })
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to save');
      }
      return res.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tenant-policies'] });
      setSaveStatus({ type: variables.policyType, status: 'success', message: 'Policy saved!' });
      setTimeout(() => setSaveStatus(null), 3000);
    },
    onError: (error: Error, variables) => {
      setSaveStatus({ type: variables.policyType, status: 'error', message: error.message });
      setTimeout(() => setSaveStatus(null), 5000);
    }
  });

  const handleSaveActionLimits = () => {
    saveMutation.mutate({ policyType: 'action_limits', config: actionLimits });
  };

  const handleSaveDataAccess = () => {
    saveMutation.mutate({ policyType: 'data_access', config: dataAccess });
  };

  const handleSaveRiskThresholds = () => {
    saveMutation.mutate({ policyType: 'risk_thresholds', config: riskThresholds });
  };

  const handleResetToDefaults = (policyType: string) => {
    if (!data?.defaults) return;
    
    switch (policyType) {
      case 'action_limits':
        setActionLimits(data.defaults.action_limits);
        break;
      case 'data_access':
        setDataAccess(data.defaults.data_access);
        break;
      case 'risk_thresholds':
        setRiskThresholds(data.defaults.risk_thresholds);
        break;
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">Failed to load policies</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-7 h-7 text-blue-600" />
            Governance Policies
          </h1>
          <p className="text-gray-600 mt-1">
            Configure safety limits and access controls for your organization
          </p>
        </div>
        <div className="flex items-center gap-2">
          {Object.values(data?.hasCustomPolicies || {}).filter(Boolean).length > 0 && (
            <Badge variant="secondary" className="bg-blue-100 text-blue-700">
              {Object.values(data?.hasCustomPolicies || {}).filter(Boolean).length} custom policies
            </Badge>
          )}
        </div>
      </div>

      {/* Policy Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="action_limits" className="flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Action Limits
          </TabsTrigger>
          <TabsTrigger value="data_access" className="flex items-center gap-2">
            <Database className="w-4 h-4" />
            Data Access
          </TabsTrigger>
          <TabsTrigger value="risk_thresholds" className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Risk Thresholds
          </TabsTrigger>
        </TabsList>

        {/* Action Limits Tab */}
        <TabsContent value="action_limits">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                Action Limits
                {data?.hasCustomPolicies?.action_limits && (
                  <Badge variant="secondary" className="ml-2">Customized</Badge>
                )}
              </CardTitle>
              <CardDescription>
                Control the financial impact and types of actions agents can propose
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Max Impact */}
              <div className="space-y-2">
                <Label>Maximum Action Impact ($)</Label>
                <Input
                  type="number"
                  placeholder="100000"
                  value={actionLimits.maxActionImpactDollars || ''}
                  onChange={(e) => setActionLimits({ 
                    ...actionLimits, 
                    maxActionImpactDollars: e.target.value ? parseInt(e.target.value) : undefined 
                  })}
                />
                <p className="text-xs text-gray-500">
                  Actions exceeding this amount will be blocked entirely
                </p>
              </div>

              {/* Approval Threshold */}
              <div className="space-y-2">
                <Label>Require Approval Above ($)</Label>
                <Input
                  type="number"
                  placeholder="25000"
                  value={actionLimits.requireApprovalForImpactAbove || ''}
                  onChange={(e) => setActionLimits({ 
                    ...actionLimits, 
                    requireApprovalForImpactAbove: e.target.value ? parseInt(e.target.value) : undefined 
                  })}
                />
                <p className="text-xs text-gray-500">
                  Actions above this threshold will require human approval
                </p>
              </div>

              {/* Max Actions Per Day */}
              <div className="space-y-2">
                <Label>Maximum Actions Per Day</Label>
                <Input
                  type="number"
                  placeholder="50"
                  value={actionLimits.maxActionsPerDay || ''}
                  onChange={(e) => setActionLimits({ 
                    ...actionLimits, 
                    maxActionsPerDay: e.target.value ? parseInt(e.target.value) : undefined 
                  })}
                />
                <p className="text-xs text-gray-500">
                  Daily limit on total action proposals
                </p>
              </div>

              {/* Forbidden Action Types */}
              <div className="space-y-2">
                <Label>Forbidden Action Types</Label>
                <div className="grid grid-cols-2 gap-2 p-3 border rounded-md bg-gray-50">
                  {ACTION_TYPES.map(action => (
                    <div key={action.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`forbid-${action.id}`}
                        checked={actionLimits.forbidActionTypes?.includes(action.id) || false}
                        onCheckedChange={(checked) => {
                          const current = actionLimits.forbidActionTypes || [];
                          if (checked) {
                            setActionLimits({ ...actionLimits, forbidActionTypes: [...current, action.id] });
                          } else {
                            setActionLimits({ ...actionLimits, forbidActionTypes: current.filter(t => t !== action.id) });
                          }
                        }}
                      />
                      <label htmlFor={`forbid-${action.id}`} className="text-sm cursor-pointer">
                        {action.label}
                      </label>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500">
                  Checked action types will be completely blocked
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 pt-4 border-t">
                <Button onClick={handleSaveActionLimits} disabled={saveMutation.isPending}>
                  <Save className="w-4 h-4 mr-2" />
                  Save Action Limits
                </Button>
                <Button variant="outline" onClick={() => handleResetToDefaults('action_limits')}>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset to Defaults
                </Button>
                {saveStatus?.type === 'action_limits' && (
                  <Badge variant={saveStatus.status === 'success' ? 'default' : 'destructive'}>
                    {saveStatus.status === 'success' ? <CheckCircle className="w-3 h-3 mr-1" /> : <AlertCircle className="w-3 h-3 mr-1" />}
                    {saveStatus.message}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Data Access Tab */}
        <TabsContent value="data_access">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5 text-purple-600" />
                Data Access
                {data?.hasCustomPolicies?.data_access && (
                  <Badge variant="secondary" className="ml-2">Customized</Badge>
                )}
              </CardTitle>
              <CardDescription>
                Control which retailers and data views are accessible
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Allowed Retailers */}
              <div className="space-y-2">
                <Label>Allowed Retailers</Label>
                <div className="space-y-2 p-3 border rounded-md bg-gray-50">
                  {RETAILERS.map(retailer => (
                    <div key={retailer.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`retailer-${retailer.id}`}
                        checked={
                          !dataAccess.allowedRetailers || 
                          dataAccess.allowedRetailers.length === 0 ||
                          dataAccess.allowedRetailers.includes(retailer.id)
                        }
                        onCheckedChange={(checked) => {
                          const current = dataAccess.allowedRetailers || [];
                          if (current.length === 0) {
                            // First restriction - all except this one
                            setDataAccess({ 
                              ...dataAccess, 
                              allowedRetailers: RETAILERS.filter(r => r.id !== retailer.id).map(r => r.id)
                            });
                          } else if (checked) {
                            setDataAccess({ ...dataAccess, allowedRetailers: [...current, retailer.id] });
                          } else {
                            const newList = current.filter(r => r !== retailer.id);
                            setDataAccess({ ...dataAccess, allowedRetailers: newList.length > 0 ? newList : undefined });
                          }
                        }}
                      />
                      <label htmlFor={`retailer-${retailer.id}`} className="text-sm cursor-pointer">
                        {retailer.label}
                      </label>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500">
                  Leave all checked for full access. Uncheck to restrict.
                </p>
              </div>

              {/* Cross-Retailer View */}
              <div className="flex items-center justify-between p-4 border rounded-md">
                <div>
                  <Label>Allow Cross-Retailer View</Label>
                  <p className="text-xs text-gray-500 mt-1">
                    Enable "Overview" mode that shows data from all retailers
                  </p>
                </div>
                <Switch
                  checked={dataAccess.allowCrossRetailerView !== false}
                  onCheckedChange={(checked) => setDataAccess({ ...dataAccess, allowCrossRetailerView: checked })}
                />
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 pt-4 border-t">
                <Button onClick={handleSaveDataAccess} disabled={saveMutation.isPending}>
                  <Save className="w-4 h-4 mr-2" />
                  Save Data Access
                </Button>
                <Button variant="outline" onClick={() => handleResetToDefaults('data_access')}>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset to Defaults
                </Button>
                {saveStatus?.type === 'data_access' && (
                  <Badge variant={saveStatus.status === 'success' ? 'default' : 'destructive'}>
                    {saveStatus.status === 'success' ? <CheckCircle className="w-3 h-3 mr-1" /> : <AlertCircle className="w-3 h-3 mr-1" />}
                    {saveStatus.message}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Risk Thresholds Tab */}
        <TabsContent value="risk_thresholds">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Risk Thresholds
                {data?.hasCustomPolicies?.risk_thresholds && (
                  <Badge variant="secondary" className="ml-2">Customized</Badge>
                )}
              </CardTitle>
              <CardDescription>
                Set confidence and inventory thresholds for recommendations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Min Confidence */}
              <div className="space-y-2">
                <Label>
                  Minimum Recommendation Confidence: {((riskThresholds.minRecommendationConfidence || 0.6) * 100).toFixed(0)}%
                </Label>
                <Slider
                  value={[(riskThresholds.minRecommendationConfidence || 0.6) * 100]}
                  onValueChange={(value) => setRiskThresholds({ 
                    ...riskThresholds, 
                    minRecommendationConfidence: value[0] / 100 
                  })}
                  min={30}
                  max={95}
                  step={5}
                  className="w-full max-w-md"
                />
                <p className="text-xs text-gray-500">
                  Recommendations below this confidence level will be blocked
                </p>
              </div>

              {/* WOS Thresholds */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Min WOS (Stockout Risk)</Label>
                  <Input
                    type="number"
                    placeholder="2"
                    value={riskThresholds.minWOSThreshold || ''}
                    onChange={(e) => setRiskThresholds({ 
                      ...riskThresholds, 
                      minWOSThreshold: e.target.value ? parseFloat(e.target.value) : undefined 
                    })}
                  />
                  <p className="text-xs text-gray-500">
                    SKUs below this WOS are flagged as stockout risk
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Max WOS (Overstock Risk)</Label>
                  <Input
                    type="number"
                    placeholder="12"
                    value={riskThresholds.maxWOSThreshold || ''}
                    onChange={(e) => setRiskThresholds({ 
                      ...riskThresholds, 
                      maxWOSThreshold: e.target.value ? parseFloat(e.target.value) : undefined 
                    })}
                  />
                  <p className="text-xs text-gray-500">
                    SKUs above this WOS are flagged as overstock risk
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 pt-4 border-t">
                <Button onClick={handleSaveRiskThresholds} disabled={saveMutation.isPending}>
                  <Save className="w-4 h-4 mr-2" />
                  Save Risk Thresholds
                </Button>
                <Button variant="outline" onClick={() => handleResetToDefaults('risk_thresholds')}>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset to Defaults
                </Button>
                {saveStatus?.type === 'risk_thresholds' && (
                  <Badge variant={saveStatus.status === 'success' ? 'default' : 'destructive'}>
                    {saveStatus.status === 'success' ? <CheckCircle className="w-3 h-3 mr-1" /> : <AlertCircle className="w-3 h-3 mr-1" />}
                    {saveStatus.message}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Info Section */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">About Governance Policies</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• <strong>Action Limits:</strong> Control financial impact and types of agent proposals</li>
                <li>• <strong>Data Access:</strong> Restrict which retailers users can view</li>
                <li>• <strong>Risk Thresholds:</strong> Set minimum confidence for recommendations</li>
                <li>• Policies take effect immediately for new agent interactions</li>
                <li>• Actions blocked by policy will show a clear explanation to users</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

